import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/store/auth";
import { Button, Card, TopBar } from "@/components/ui";
import { Icons } from "@/components/Icons";

export function EditProfileScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      await updateProfile({
        name: name.trim() || undefined,
        email: email.trim() || undefined,
      });
      navigate("/profile", { replace: true });
    } catch (err) {
      setError(t("v2.profile.err_save_profile", "Couldn't save your profile. Try again."));
    } finally {
      setBusy(false);
    }
  }

  const hasChanges = (name.trim() !== (user?.name || "")) || (email.trim() !== (user?.email || ""));

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar title={t("v2.profile.edit_profile", "Edit Profile")} onBack={() => navigate(-1)} />
      
      <div
        style={{
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Card padding={20} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-2)",
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              {t("v2.signin.name_label", "Full Name")}
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-card)",
                padding: "0 14px",
                height: 48,
              }}
            >
              <Icons.user size={18} style={{ color: "var(--ink-3)" }} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("v2.signin.name_placeholder", "Enter your name")}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  fontSize: 15,
                  color: "var(--ink-1)",
                  fontFamily: "inherit",
                  width: "100%",
                }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 700,
                color: "var(--ink-2)",
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              {t("v2.profile.email_label", "Email Address")}
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-card)",
                padding: "0 14px",
                height: 48,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--ink-3)" }}>
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("v2.profile.email_placeholder", "Enter your email")}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  fontSize: 15,
                  color: "var(--ink-1)",
                  fontFamily: "inherit",
                  width: "100%",
                }}
              />
            </div>
          </div>
        </Card>

        {error && (
          <div style={{ color: "var(--danger-600)", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
            {error}
          </div>
        )}

        <Button
          full
          size="lg"
          onClick={handleSave}
          disabled={busy || !hasChanges}
        >
          {busy ? t("v2.common.saving", "Saving…") : t("v2.common.save", "Save Changes")}
        </Button>
      </div>
    </div>
  );
}
